import { SERVICES_TAGS } from 'constants/servicesTags';
import initialApiService from 'services/initialApiService';

import {
  AllContactsResType,
  ContactDetailReqType,
  ContactDetailResType,
  DevelopmentCreateReqType,
  DevelopmentDetailType,
  DevelopmentListResType,
  MessageType,
  ReadMessageReqType,
  SendMessageReqType,
} from 'types/promoters/promoterTypes';
import { uploadImageType, uploadResourceType } from 'types/property/propertyTypes';

const promoterApiService = initialApiService.injectEndpoints({
  endpoints: (builder) => ({
    createDevelopment: builder.mutation<DevelopmentDetailType, DevelopmentCreateReqType>({
      query: (data) => ({
        url: `/propertydevelopments/create`,
        method: 'POST',
        body: data,
      }),
    }),
    editDevelopment: builder.mutation<DevelopmentDetailType, DevelopmentCreateReqType>({
      query: (data) => ({
        url: `/propertydevelopments/edit`,
        method: 'PATCH',
        body: data,
      }),
    }),
    deleteDevelopment: builder.mutation<string, string>({
      query: (id) => ({
        url: `propertydevelopments/delete/${id}`,
        method: 'DELETE',
      }),
    }),
    hideShowDevelopment: builder.mutation<DevelopmentDetailType, { id: string; hide: boolean }>({
      query: (data) => ({
        url: `/propertydevelopments/visibilitystatus`,
        method: 'PUT',
        body: data,
      }),
    }),
    getDevelopmentList: builder.mutation<
      DevelopmentListResType,
      { pageNumber: number; pageSize: number }
    >({
      query: (data) => ({
        url: `/propertydevelopments/list`,
        method: 'POST',
        body: data,
      }),
    }),
    sendMessage: builder.mutation<MessageType, SendMessageReqType>({
      query: (data) => ({
        url: `/messages/send`,
        method: 'POST',
        body: data,
      }),
    }),
    getMessages: builder.query<MessageType[], ReadMessageReqType>({
      query: (data) => ({
        url: `/messages`,
        method: 'GET',
        params: data,
      }),
    }),
    getReplyMessages: builder.query<MessageType[], { id: string }>({
      query: (data) => ({
        url: `/replies`,
        method: 'GET',
        params: data,
      }),
    }),
    readMessage: builder.mutation<MessageType, string>({
      query: (id) => ({
        url: `/messages/read/${id}`,
        method: 'PUT',
      }),
    }),

    deleteMessage: builder.mutation<MessageType, string>({
      query: (id) => ({
        url: `/messages/delete/${id}`,
        method: 'DELETE',
      }),
    }),

    getUserDevelopmentList: builder.mutation<
      DevelopmentListResType,
      { pageNumber: number; pageSize: number }
    >({
      query: (data) => ({
        url: `/users/developments`,
        method: 'POST',
        body: data,
      }),
    }),
    getDevelopmentDetail: builder.query<DevelopmentDetailType, string>({
      query: (slug) => ({
        url: `/propertydevelopments/${slug}`,
        method: 'GET',
      }),
    }),
    uploadDevelopmentImage: builder.mutation<DevelopmentDetailType, uploadImageType>({
      query: (body) => ({
        url: 'propertydevelopments/image',
        method: 'POST',
        body,
      }),
    }),
    deleteDevelopmentImage: builder.mutation<DevelopmentDetailType, string>({
      query: (id) => ({
        url: `propertydevelopments/image/${id}`,
        method: 'DELETE',
      }),
    }),
    uploadDevelopmentResource: builder.mutation<DevelopmentDetailType, uploadResourceType>({
      query: (body) => ({
        url: 'propertydevelopments/resource',
        method: 'POST',
        body,
      }),
    }),
    deleteDevelopmentResource: builder.mutation<DevelopmentDetailType, string>({
      query: (id) => ({
        url: `propertydevelopments/resource/${id}`,
        method: 'DELETE',
      }),
    }),
    createContacts: builder.mutation<ContactDetailResType, ContactDetailReqType>({
      query: (data) => ({
        url: `/servicecontacts`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [SERVICES_TAGS.CONTACTS],
    }),
    editContacts: builder.mutation<ContactDetailResType, ContactDetailReqType>({
      query: (body) => ({
        url: `/servicecontacts/${body.id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: [SERVICES_TAGS.CONTACTS],
    }),
    getContactDetail: builder.query<ContactDetailResType, string>({
      query: (id) => ({
        url: `/servicecontacts/${id}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, id) => [{ type: SERVICES_TAGS.CONTACTS, id }],
    }),
    getAllContacts: builder.query<AllContactsResType, string[]>({
      query: (serviceContactSlug) => ({
        url: `/servicecontacts/list`,
        method: 'POST',
        body: { serviceContactSlug }, // now passed as JSON body
      }),
      providesTags: [SERVICES_TAGS.CONTACTS],
    }),
    deleteContact: builder.mutation<ContactDetailResType, string>({
      query: (id) => ({
        url: `/servicecontacts/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: [SERVICES_TAGS.CONTACTS],
    }),
    deleteContactImage: builder.mutation<ContactDetailResType, string>({
      query: (id) => ({
        url: `/servicecontacts/${id}/profilephoto`,
        method: 'DELETE',
      }),
    }),
  }),
});

export const {
  useSendMessageMutation,
  useLazyGetMessagesQuery,
  useLazyGetReplyMessagesQuery,
  useReadMessageMutation,
  useDeleteMessageMutation,
  useCreateDevelopmentMutation,
  useGetUserDevelopmentListMutation,
  useDeleteDevelopmentMutation,
  useHideShowDevelopmentMutation,
  useEditDevelopmentMutation,
  useGetDevelopmentListMutation,
  useLazyGetDevelopmentDetailQuery,
  useUploadDevelopmentImageMutation,
  useDeleteDevelopmentImageMutation,
  useDeleteDevelopmentResourceMutation,
  useUploadDevelopmentResourceMutation,
  useCreateContactsMutation,
  useEditContactsMutation,
  useLazyGetContactDetailQuery,
  useLazyGetAllContactsQuery,
  useDeleteContactMutation,
  useDeleteContactImageMutation,
} = promoterApiService;
